import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { scrollToSection, scrollToTop } from "./scrollToSection";

const SECTIONS = [
  { id: "work", label: "Work" },
  { id: "projects", label: "Projects" },
];

function SiteNav() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [overHero, setOverHero] = useState(
    location.pathname === "/" || location.pathname === "/resume"
  );
  const [current, setCurrent] = useState("");

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const update = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      if (location.pathname === "/resume") {
        setOverHero(true);
        return;
      }
      if (location.pathname !== "/") {
        setOverHero(false);
        return;
      }
      const hero = document.querySelector(".hero");
      const limit = hero ? Math.max(hero.offsetHeight - 72, 72) : window.innerHeight * 0.7;
      setOverHero(y < limit);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointer = (event) => {
      if (!event.target.closest(".site-header")) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  useEffect(() => {
    if (location.pathname === "/resume") {
      setCurrent("resume");
      return undefined;
    }

    setCurrent("");

    const nodes = SECTIONS.map((item) => document.getElementById(item.id)).filter(Boolean);
    if (!nodes.length || typeof IntersectionObserver !== "function") {
      setCurrent("");
      return undefined;
    }

    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visible.set(entry.target.id, entry.isIntersecting);
        });
        const active = SECTIONS.find((item) => visible.get(item.id));
        setCurrent(active ? active.id : "");
      },
      { rootMargin: "-18% 0px -68% 0px", threshold: [0.05, 0.2, 0.45] }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [location.pathname]);

  const goToSection = (id) => (event) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    setOpen(false);
    if (location.pathname === `/${id}`) {
      event.preventDefault();
      scrollToSection(id, "smooth");
    }
  };

  return (
    <header
      className={`site-header${scrolled ? " is-scrolled" : ""}${open ? " is-open" : ""}${
        overHero ? " is-over-hero" : ""
      }`}
    >
      <button
        type="button"
        className="skip-link"
        onClick={() => {
          const main = document.getElementById("content");
          if (!main) return;
          main.focus();
        }}
      >
        Skip to content
      </button>
      <div className="site-header-bar">
        <Link
          className="wordmark"
          to="/"
          onClick={() => {
            setOpen(false);
            if (location.pathname === "/") scrollToTop("smooth");
          }}
        >
          Max Kotas
        </Link>
        <button
          type="button"
          className="nav-toggle"
          aria-expanded={open}
          aria-controls="site-nav"
          onClick={() => setOpen((value) => !value)}
        >
          <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
          <span className="nav-toggle-bars" aria-hidden="true" />
        </button>
        <nav id="site-nav" className={open ? "is-open" : undefined} aria-label="Primary">
          {SECTIONS.map((item) => (
            <Link
              key={item.id}
              to={`/${item.id}`}
              state={{ smooth: true }}
              aria-current={current === item.id ? "true" : undefined}
              onClick={goToSection(item.id)}
            >
              {item.label}
            </Link>
          ))}
          <Link to="/resume" aria-current={current === "resume" ? "page" : undefined}>
            Resume
          </Link>
        </nav>
      </div>
    </header>
  );
}

export default SiteNav;
